import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Option } from '@sapphire/result';
import {
  EMPTY,
  exhaustMap,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import { SurveyService } from '@ps/survey-data-access';
import { canSeeTargetMargin } from '@ps/survey-utils';
import { TemplateService } from '@ps/survey-template-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';

import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
} from './survey-creation-audience.actions';
import { CHILD_SCOPE } from '../survey-creation-child-registry.helper';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
} from '../core/survey-creation-core.actions';
import { templateMissingBuyerCpiWarning } from './domain/template-buyer-cpi';

@Injectable()
export class SurveyCreationTemplateEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _surveyService = inject(SurveyService);
  private readonly _templateService = inject(TemplateService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _toastService = inject(ToastService);

  public readonly createSurveyFromTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.createSurveyFromTemplate),
      exhaustMap(({ templateId, deleteSurveyId }) =>
        this._surveyService
          .createSurveyFromTemplate(
            templateId,
            this._authSelectors.user().unwrap().id,
            this._authSelectors.activeCompanyId(),
          )
          .pipe(
            map((survey) => {
              this._toastService.success('Survey created from template', {
                keepOpen: false,
              });
              return SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess(
                {
                  survey,
                  replacedSurveyId: deleteSurveyId,
                },
              );
            }),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationAudienceApiActions.createSurveyFromTemplateFailure(
                    {
                      error: error.message,
                    },
                  ),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  // The edit page reloads the survey after creation, so the price has to land
  // on the loaded survey, not the one the create call returned.
  public readonly applyTemplateBuyerCpi$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess),
      filter(() => this._canSeeTargetMargin()),
      tap(({ survey }) =>
        templateMissingBuyerCpiWarning(survey.offerPrice ?? 0).inspect(
          (message) => this._toastService.warning(message, { keepOpen: false }),
        ),
      ),
      mergeMap(({ survey }) =>
        Option.from(survey.templateId).mapOr(EMPTY, (templateId) =>
          this._templateService.templateOfferPrice(templateId).pipe(
            filter((templateOfferPrice) => templateOfferPrice > 0),
            mergeMap((templateOfferPrice) =>
              this._applyOnceLoaded(survey.id, templateOfferPrice),
            ),
            this.psCatchError(() => EMPTY, { notify: true }),
          ),
        ),
      ),
    ),
  );

  private _applyOnceLoaded(
    surveyId: number,
    offerPrice: number,
  ): Observable<Action> {
    return this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      filter(({ survey }) => survey.id === surveyId),
      take(1),
      map(() =>
        SurveyCreationCoreActions.patchActiveSurvey({
          patch: { offerPrice },
          scope: CHILD_SCOPE.ACTIVE,
        }),
      ),
    );
  }

  private _canSeeTargetMargin(): boolean {
    return canSeeTargetMargin({
      hasImpersonatedBuyer: this._authSelectors.hasImpersonatedBuyer(),
      hasTargetMargin:
        this._userSettingsSelectors.buyerSettings().enableTargetMargin,
    });
  }
}
